import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useState,
} from 'react';

import { Icon } from '../icon';
import { Typography } from '../typography';

import styles from './header-item.scss';

type HeaderItemProps = RefAttributes<HTMLButtonElement> &
  HTMLAttributes<HTMLButtonElement> & {
    icon?: string;
    active?: boolean;
    counter?: number;
    dataTestId?: string;
    labelHidden?: boolean;
  };

const COMPONENT_ID = 'modulor-header-item';

export const HeaderItem: ForwardRefExoticComponent<HeaderItemProps> = forwardRef<
  HTMLButtonElement,
  HeaderItemProps
>(
  (
    {
      icon,
      active,
      counter,
      labelHidden,
      children,
      dataTestId = COMPONENT_ID,
      style,
      className,
      ...rest
    },
    ref,
  ) => {
    const [iconName, setIconName] = useState('');

    const itemClasses = classNames(className, styles.container, {
      [styles.active]: active,
      [styles['no-label']]: !children || labelHidden,
    });

    useEffect(() => {
      if (!icon) return;

      const resolvedIconName = active ? icon.replace('outlined', 'filled') : icon;
      setIconName(resolvedIconName);
    }, [active, icon]);

    return (
      <button
        className={itemClasses}
        ref={ref}
        {...rest}
        style={style}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}>
        {iconName ? (
          <Icon name={iconName} size={24} count={counter} className={styles.icon} />
        ) : null}
        {children !== null && !labelHidden ? (
          <Typography variant="subhead-semibold" className={styles.label}>
            {children}
          </Typography>
        ) : null}
      </button>
    );
  },
);

HeaderItem.displayName = 'HeaderItem';
